import React, { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import InfoSection from "./InfoSection";
import InfoRow from "./InfoRow";
import axiosInstance from "../../../../../axios/axiosInstance";
import apiEndpoints from "../../../../../axios/api";
import { notify } from "../../../../../utils/notification";
import { getCoords } from "../../../../../utils/helpers/geolocation";
import SearchableDropdown from "../../../../../components/SearchableDropdown";

// Attendance & geofence settings — PATCH /api/societies/:id/geofence.
//
// Self-contained (own edit state + request) because this endpoint has
// partial-update semantics distinct from the big profile payload: we send
// ONLY the fields the admin changed, so e.g. toggling geofencing off never
// clears the stored coordinates.
//
// Server-side business rule mirrored here: the final state may not be
// "geofence enabled with no center point", so switching that toggle on while
// the society has no stored coordinates requires lat/lng in the same request.

const SELFIE_MODES = [
    { value: "OFF", label: "Off — no photo" },
    { value: "OPTIONAL", label: "Optional — photo if provided" },
    { value: "REQUIRED", label: "Required — punch needs a photo" },
];

const selfieModeLabel = (mode) =>
    SELFIE_MODES.find(m => m.value === mode)?.label || mode || "Off — no photo";

const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`;

const DAY_START_HOUR_OPTIONS = Array.from({ length: 24 }, (_, h) => ({
    value: String(h),
    label: `${hourLabel(h)}${h === 0 ? " (midnight)" : ""}`,
}));

const toNumOrNull = (v) => (v === "" || v === null || v === undefined ? null : Number(v));

// IANA validation without shipping a timezone list: the Intl constructor
// throws on unknown zone names.
const isValidTimezone = (tz) => {
    try {
        new Intl.DateTimeFormat(undefined, { timeZone: tz });
        return true;
    } catch {
        return false;
    }
};

const TIMEZONE_SUGGESTIONS =
    typeof Intl.supportedValuesOf === "function" ? Intl.supportedValuesOf("timeZone") : [];

const GeofenceSettings = ({ societyId, data, onSaved }) => {
    // Stored values (raw society row; DECIMALs may arrive as strings)
    const stored = {
        geofence_enabled: Number(data?.geofence_enabled) === 1 ? 1 : 0,
        geofence_enforce: Number(data?.geofence_enforce) === 1 ? 1 : 0,
        geofence_latitude: data?.geofence_latitude != null ? Number(data.geofence_latitude) : null,
        geofence_longitude: data?.geofence_longitude != null ? Number(data.geofence_longitude) : null,
        geofence_radius_meters: data?.geofence_radius_meters != null ? Number(data.geofence_radius_meters) : null,
        attendance_enabled: Number(data?.attendance_enabled) === 1 ? 1 : 0,
        attendance_day_start_hour: data?.attendance_day_start_hour != null ? Number(data.attendance_day_start_hour) : 0,
        timezone: data?.timezone || null,
        attendance_selfie_mode: data?.attendance_selfie_mode || "OFF",
        attendance_work_log_enabled: Number(data?.attendance_work_log_enabled) === 1 ? 1 : 0,
    };
    const hasStoredCenter = stored.geofence_latitude != null && stored.geofence_longitude != null;

    const [isEditing, setIsEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [locating, setLocating] = useState(false);
    const [form, setForm] = useState(null);
    const [fieldErrors, setFieldErrors] = useState({});

    const handleEdit = () => {
        setForm({
            attendanceEnabled: stored.attendance_enabled === 1,
            workLogEnabled: stored.attendance_work_log_enabled === 1,
            selfieMode: stored.attendance_selfie_mode,
            dayStartHour: String(stored.attendance_day_start_hour),
            timezone: stored.timezone || "",
            enabled: stored.geofence_enabled === 1,
            enforce: stored.geofence_enforce === 1,
            latitude: stored.geofence_latitude != null ? String(stored.geofence_latitude) : "",
            longitude: stored.geofence_longitude != null ? String(stored.geofence_longitude) : "",
            radius: stored.geofence_radius_meters != null ? String(stored.geofence_radius_meters) : "200",
        });
        setFieldErrors({});
        setIsEditing(true);
    };

    const handleCancel = () => {
        setIsEditing(false);
        setForm(null);
        setFieldErrors({});
    };

    const setField = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setFieldErrors(prev => ({ ...prev, [field]: undefined }));
    };

    const handleUseMyLocation = async () => {
        setLocating(true);
        const coords = await getCoords();
        setLocating(false);
        if (coords.latitude == null) {
            notify("error", "Couldn't get your location — allow location access and try again");
            return;
        }
        setForm(prev => ({
            ...prev,
            latitude: String(coords.latitude.toFixed(6)),
            longitude: String(coords.longitude.toFixed(6)),
        }));
        setFieldErrors(prev => ({ ...prev, latitude: undefined, longitude: undefined }));
    };

    const validate = () => {
        const errors = {};
        const lat = toNumOrNull(form.latitude);
        const lng = toNumOrNull(form.longitude);
        const radius = toNumOrNull(form.radius);
        const tz = form.timezone.trim();

        if (lat != null && (Number.isNaN(lat) || lat < -90 || lat > 90)) {
            errors.latitude = "Latitude must be between -90 and 90";
        }
        if (lng != null && (Number.isNaN(lng) || lng < -180 || lng > 180)) {
            errors.longitude = "Longitude must be between -180 and 180";
        }
        // Coordinates travel as a pair
        if ((lat == null) !== (lng == null)) {
            const missing = lat == null ? "latitude" : "longitude";
            errors[missing] = "Enter both latitude and longitude, or neither";
        }
        if (radius != null && (Number.isNaN(radius) || !Number.isInteger(radius) || radius < 10 || radius > 100000)) {
            errors.radius = "Radius must be a whole number between 10 and 100000";
        }
        if (tz && (tz.length > 64 || !isValidTimezone(tz))) {
            errors.timezone = "Must be a valid IANA timezone, e.g. Asia/Kolkata";
        }
        // BE rejects a final state of geofence-enabled-with-no-center
        if (form.enabled && !hasStoredCenter && (lat == null || lng == null)) {
            if (lat == null) errors.latitude = "Required to enable geofencing";
            if (lng == null) errors.longitude = "Required to enable geofencing";
        }
        return errors;
    };

    const buildPayload = () => {
        const payload = {};
        const lat = toNumOrNull(form.latitude);
        const lng = toNumOrNull(form.longitude);
        const radius = toNumOrNull(form.radius);
        const tz = form.timezone.trim() || null;
        const dayStartHour = Number(form.dayStartHour);

        const attendanceEnabled = form.attendanceEnabled ? 1 : 0;
        const workLogEnabled = form.workLogEnabled ? 1 : 0;
        if (attendanceEnabled !== stored.attendance_enabled) payload.attendance_enabled = attendanceEnabled;
        if (form.selfieMode !== stored.attendance_selfie_mode) payload.attendance_selfie_mode = form.selfieMode;
        if (workLogEnabled !== stored.attendance_work_log_enabled) payload.attendance_work_log_enabled = workLogEnabled;
        if (dayStartHour !== stored.attendance_day_start_hour) payload.attendance_day_start_hour = dayStartHour;
        // Clearing the field resets to the app default (null)
        if (tz !== stored.timezone) payload.timezone = tz;

        const enabled = form.enabled ? 1 : 0;
        const enforce = form.enforce ? 1 : 0;
        if (enabled !== stored.geofence_enabled) payload.geofence_enabled = enabled;
        if (enforce !== stored.geofence_enforce) payload.geofence_enforce = enforce;
        // Only send coordinates the admin actually provided/changed — omitting
        // them preserves the stored center (partial-update semantics).
        if (lat != null && lat !== stored.geofence_latitude) payload.geofence_latitude = lat;
        if (lng != null && lng !== stored.geofence_longitude) payload.geofence_longitude = lng;
        if (radius != null && radius !== stored.geofence_radius_meters) payload.geofence_radius_meters = radius;
        return payload;
    };

    const handleSave = async () => {
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setFieldErrors(errors);
            return;
        }

        const payload = buildPayload();
        if (Object.keys(payload).length === 0) {
            handleCancel();
            return;
        }

        setSaving(true);
        try {
            const url = apiEndpoints.UPDATE_SOCIETY_GEOFENCE_API.replace(":id", societyId);
            const res = await axiosInstance.patch(url, payload);
            if (res?.success) {
                notify("success", res.message || "Attendance settings updated successfully");
                setIsEditing(false);
                setForm(null);
                onSaved?.();
            } else {
                notify("error", res?.message || "Failed to update settings");
            }
        } catch (err) {
            const body = err?.response?.data;
            // Surface field-level validation messages when the BE provides them
            const detail = Array.isArray(body?.errors)
                ? body.errors.map(e => e.msg || e.message).filter(Boolean).join("; ")
                : null;
            notify("error", detail || body?.message || err?.message || "Failed to update settings");
        } finally {
            setSaving(false);
        }
    };

    const inputClass = (err) =>
        `tw-w-full tw-border tw-rounded-lg tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 ${err ? "tw-border-red-300 focus:tw-ring-red-400" : "tw-border-slate-200 focus:tw-ring-amber-400"}`;

    const Toggle = ({ checked, onChange, label, hint }) => (
        <label className="tw-flex tw-items-start tw-gap-3 tw-cursor-pointer tw-select-none">
            <span
                role="switch"
                aria-checked={checked}
                onClick={(e) => { e.preventDefault(); onChange(!checked); }}
                className={`tw-mt-0.5 tw-w-9 tw-h-5 tw-rounded-full tw-flex-shrink-0 tw-relative tw-transition-colors ${checked ? "tw-bg-amber-500" : "tw-bg-slate-200"}`}
            >
                <span className={`tw-absolute tw-top-0.5 tw-w-4 tw-h-4 tw-rounded-full tw-bg-white tw-shadow tw-transition-all ${checked ? "tw-left-[18px]" : "tw-left-0.5"}`} />
            </span>
            <span>
                <span className="tw-block tw-text-sm tw-font-medium tw-text-slate-700">{label}</span>
                {hint && <span className="tw-block tw-text-xs tw-text-slate-400 tw-mt-0.5">{hint}</span>}
            </span>
        </label>
    );

    const SubHeading = ({ children }) => (
        <p className="tw-text-xs tw-font-semibold tw-text-slate-400 tw-uppercase tw-tracking-wider tw-mb-0 tw-mt-1">{children}</p>
    );

    return (
        <InfoSection
            title="Attendance & Geofence"
            icon="solar:map-point-bold"
            isEditing={isEditing}
            isLoading={saving}
            onEdit={handleEdit}
            onSave={handleSave}
            onCancel={handleCancel}
        >
            {!isEditing ? (
                <div className="row">
                    <InfoRow
                        label="Attendance punching"
                        value={stored.attendance_enabled === 1 ? "Enabled" : "Disabled"}
                        icon="solar:clock-circle-bold-duotone"
                        colSize="col-md-6"
                    />
                    <InfoRow
                        label="Punch selfie"
                        value={selfieModeLabel(stored.attendance_selfie_mode)}
                        icon="solar:camera-bold-duotone"
                        colSize="col-md-6"
                    />
                    <InfoRow
                        label="Shift work log"
                        value={stored.attendance_work_log_enabled === 1 ? "Enabled" : "Disabled"}
                        icon="solar:notebook-bold-duotone"
                        colSize="col-md-6"
                    />
                    <InfoRow
                        label="Attendance day starts at"
                        value={`${hourLabel(stored.attendance_day_start_hour)}${stored.attendance_day_start_hour === 0 ? " (midnight)" : ""}`}
                        icon="solar:sunrise-bold-duotone"
                        colSize="col-md-6"
                    />
                    <InfoRow
                        label="Timezone"
                        value={stored.timezone || "App default"}
                        icon="solar:global-bold-duotone"
                        colSize="col-md-6"
                    />
                    <InfoRow
                        label="Geofencing"
                        value={stored.geofence_enabled === 1 ? "Enabled" : "Disabled"}
                        icon="solar:map-point-wave-bold-duotone"
                        colSize="col-md-6"
                    />
                    <InfoRow
                        label="Outside-radius punches"
                        value={
                            stored.geofence_enabled !== 1
                                ? "—"
                                : stored.geofence_enforce === 1
                                    ? "Blocked (strict)"
                                    : "Allowed, flagged for approval"
                        }
                        icon="solar:shield-check-bold-duotone"
                        colSize="col-md-6"
                    />
                    <InfoRow
                        label="Center point"
                        value={hasStoredCenter
                            ? `${stored.geofence_latitude.toFixed(6)}, ${stored.geofence_longitude.toFixed(6)}`
                            : "Not set"}
                        icon="solar:gps-bold-duotone"
                        colSize="col-md-6"
                    />
                    <InfoRow
                        label="Allowed radius"
                        value={stored.geofence_radius_meters != null ? `${stored.geofence_radius_meters} m` : "—"}
                        icon="solar:radar-2-bold-duotone"
                        colSize="col-md-6"
                    />
                </div>
            ) : (
                <div className="tw-flex tw-flex-col tw-gap-4">
                    <SubHeading>Attendance</SubHeading>
                    <Toggle
                        checked={form.attendanceEnabled}
                        onChange={(v) => setField("attendanceEnabled", v)}
                        label="Enable attendance punching"
                        hint="Lets active employees of this society punch in and out of shifts."
                    />
                    <Toggle
                        checked={form.workLogEnabled}
                        onChange={(v) => setField("workLogEnabled", v)}
                        label="Enable shift work log"
                        hint="Employees record what they worked on for the shift when punching out."
                    />

                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1">Punch selfie</label>
                            <SearchableDropdown
                                value={form.selfieMode}
                                onChange={(v) => setField("selfieMode", v)}
                                options={SELFIE_MODES}
                                clearable={false}
                                placeholder="Select mode"
                            />
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1">Attendance day starts at</label>
                            <SearchableDropdown
                                value={form.dayStartHour}
                                onChange={(v) => setField("dayStartHour", v)}
                                options={DAY_START_HOUR_OPTIONS}
                                clearable={false}
                                placeholder="Select hour"
                            />
                            <p className="tw-text-xs tw-text-slate-400 tw-mt-1 tw-mb-0">Use a morning hour for night rosters so overnight shifts stay on one day.</p>
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1">Timezone</label>
                            {TIMEZONE_SUGGESTIONS.length > 0 ? (
                                <SearchableDropdown
                                    value={form.timezone}
                                    // The dropdown's clear button emits 'all' (filter
                                    // convention) — map it back to empty = app default.
                                    onChange={(v) => setField("timezone", v === "all" ? "" : v)}
                                    options={TIMEZONE_SUGGESTIONS}
                                    placeholder="App default (e.g. Asia/Kolkata)"
                                />
                            ) : (
                                // Older browsers without Intl.supportedValuesOf: free
                                // text, validated on save via Intl.DateTimeFormat.
                                <input
                                    type="text"
                                    value={form.timezone}
                                    onChange={(e) => setField("timezone", e.target.value)}
                                    placeholder="App default (e.g. Asia/Kolkata)"
                                    className={inputClass(fieldErrors.timezone)}
                                />
                            )}
                            {fieldErrors.timezone
                                ? <p className="tw-text-xs tw-text-red-500 tw-mt-1 tw-mb-0">{fieldErrors.timezone}</p>
                                : <p className="tw-text-xs tw-text-slate-400 tw-mt-1 tw-mb-0">Clear the field to use the app default.</p>}
                        </div>
                    </div>

                    <SubHeading>Geofence</SubHeading>
                    <Toggle
                        checked={form.enabled}
                        onChange={(v) => setField("enabled", v)}
                        label="Enable attendance geofencing"
                        hint="Employee punches are checked against the center point and radius below."
                    />
                    <Toggle
                        checked={form.enforce}
                        onChange={(v) => setField("enforce", v)}
                        label="Strictly block punches outside the radius"
                        hint="Off: out-of-range punches are still accepted but flagged for admin approval."
                    />

                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-3">
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1">
                                Latitude {form.enabled && !hasStoredCenter && <span className="tw-text-red-500">*</span>}
                            </label>
                            <input
                                type="number"
                                step="any"
                                min="-90"
                                max="90"
                                value={form.latitude}
                                onChange={(e) => setField("latitude", e.target.value)}
                                placeholder="e.g. 19.119000"
                                className={inputClass(fieldErrors.latitude)}
                            />
                            {fieldErrors.latitude && <p className="tw-text-xs tw-text-red-500 tw-mt-1 tw-mb-0">{fieldErrors.latitude}</p>}
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1">
                                Longitude {form.enabled && !hasStoredCenter && <span className="tw-text-red-500">*</span>}
                            </label>
                            <input
                                type="number"
                                step="any"
                                min="-180"
                                max="180"
                                value={form.longitude}
                                onChange={(e) => setField("longitude", e.target.value)}
                                placeholder="e.g. 72.846400"
                                className={inputClass(fieldErrors.longitude)}
                            />
                            {fieldErrors.longitude && <p className="tw-text-xs tw-text-red-500 tw-mt-1 tw-mb-0">{fieldErrors.longitude}</p>}
                        </div>
                        <div>
                            <label className="tw-block tw-text-xs tw-font-medium tw-text-slate-500 tw-mb-1">Radius (meters)</label>
                            <input
                                type="number"
                                min="10"
                                max="100000"
                                step="1"
                                value={form.radius}
                                onChange={(e) => setField("radius", e.target.value)}
                                placeholder="10 – 100000"
                                className={inputClass(fieldErrors.radius)}
                            />
                            {fieldErrors.radius && <p className="tw-text-xs tw-text-red-500 tw-mt-1 tw-mb-0">{fieldErrors.radius}</p>}
                        </div>
                    </div>

                    <div>
                        <button
                            type="button"
                            onClick={handleUseMyLocation}
                            disabled={locating}
                            className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-rounded-lg tw-text-xs tw-font-medium tw-text-amber-600 tw-bg-amber-50 hover:tw-bg-amber-100 tw-transition-colors disabled:tw-opacity-50"
                        >
                            <Icon icon={locating ? "solar:refresh-bold" : "solar:gps-bold"} className={locating ? "tw-animate-spin" : ""} />
                            {locating ? "Getting location…" : "Use my current location"}
                        </button>
                        <p className="tw-text-xs tw-text-slate-400 tw-mt-2 tw-mb-0">
                            Only changed fields are saved. Disabling geofencing keeps the stored
                            center point, so you can re-enable it later without re-entering coordinates.
                        </p>
                    </div>
                </div>
            )}
        </InfoSection>
    );
};

export default GeofenceSettings;
